import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  Activity, 
  Smartphone, 
  Users, 
  MapPin, 
  AlertTriangle, 
  TrendingUp, 
  Building2,
  Zap,
  Clock,
  Radio
} from 'lucide-react';
import { toast } from 'react-toastify';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const { get } = useApi();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  // Get admin ID from user object
  const getAdminId = () => {
    return user?.id || user?._id || user?.admin_id || user?.userId || user?.user_id || null;
  };

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const dateParam = getDateParam();
      const adminId = getAdminId();
      
      // Build query parameters
      const params = new URLSearchParams({
        date: dateParam
      });
      
      // Add admin_id if available
      if (adminId) {
        params.append('admin_id', adminId);
      }
      
      const response = await get(`/admin/dashboard?${params.toString()}`);
      setDashboardData(response);
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      setIsLoading(false);
    }
  };

  const periodOptions = [
    { value: 'today', label: 'Today', apiParam: 'today' },
    { value: 'lastWeek', label: 'Last Week', apiParam: 'last_week' },
    { value: 'thisWeek', label: 'This Week', apiParam: 'this_week' },
    { value: 'lastMonth', label: 'Last Month', apiParam: 'last_month' },
    { value: 'thisMonth', label: 'This Month', apiParam: 'this_month' }
  ];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    fetchDashboardData();
  };

  // Map period to API date parameter
  const getDateParam = () => {
    const selectedOption = periodOptions.find(opt => opt.value === selectedPeriod);
    return selectedOption?.apiParam || 'today';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get alert type color
  const getAlertTypeColor = (alertType) => {
    switch (alertType) {
      case 'SOS Alert':
        return 'bg-red-500';
      case 'Ignition ON':
        return 'bg-green-500';
      case 'Ignition OFF':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  // Summary cards configuration - Superadmin only (all 5 cards)
  const getSummaryCards = () => {
    if (!dashboardData?.summary) return [];
    
    const { summary } = dashboardData;
    return [
      { 
        title: 'Total Devices', 
        value: summary.total_devices || 0, 
        icon: Smartphone,
        color: 'bg-blue-500',
        bgGradient: 'from-blue-500 to-blue-600',
        description: 'Registered devices',
        route: '/admin/devices'
      },
      { 
        title: 'Total Accounts', 
        value: summary.total_accounts || 0, 
        icon: Users,
        color: 'bg-purple-500',
        bgGradient: 'from-purple-500 to-purple-600',
        description: 'Active accounts',
        route: '/admin/accounts'
      },
      { 
        title: 'Total Trips', 
        value: summary.total_trips || 0, 
        icon: MapPin,
        color: 'bg-green-500',
        bgGradient: 'from-green-500 to-green-600',
        description: 'All time trips',
        route: '/admin/map/trips'
      },
      { 
        title: 'Active Trips', 
        value: summary.active_trips || 0, 
        icon: Activity,
        color: 'bg-orange-500',
        bgGradient: 'from-orange-500 to-orange-600',
        description: 'Currently active',
        route: '/admin/map/trips'
      },
      { 
        title: 'Total Alerts', 
        value: summary.total_alerts || 0, 
        icon: AlertTriangle,
        color: 'bg-red-500',
        bgGradient: 'from-red-500 to-red-600',
        description: 'System alerts',
        route: null // No specific alerts page, can scroll to alerts section if needed
      }
    ];
  };

  const handleCardClick = (route) => {
    // Only allow navigation for superadmin role
    const isSuperAdmin = hasRole('super_admin') || hasRole('superadmin');
    if (route && isSuperAdmin) {
      navigate(route);
    }
  };

  // Trip Trends Chart Data
  const getTripTrendsChartData = () => {
    if (!dashboardData?.trip_trends || dashboardData.trip_trends.length === 0) {
      return {
        labels: [],
        datasets: [{
          label: 'Trips',
          data: [],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
        }]
      };
    }

    const trends = dashboardData.trip_trends;
    return {
      labels: trends.map(t => formatDate(t.date)),
      datasets: [{
        label: 'Trips',
        data: trends.map(t => t.total),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }]
    };
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          stepSize: 1,
        }
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  // Device Status Chart Data - Using only API data
  const getDeviceStatusChartData = () => {
    if (!dashboardData?.summary) {
      return {
        labels: ['Active Trips', 'Completed Trips'],
        datasets: [{
          label: 'Trip Count',
          data: [0, 0],
          backgroundColor: ['rgb(34, 197, 94)', 'rgb(239, 68, 68)'],
          borderWidth: 0,
          borderRadius: 4,
        }]
      };
    }

    // Use only API data - no hardcoded values
    const { total_trips, active_trips } = dashboardData.summary;
    const active = active_trips || 0;
    const completed = Math.max(0, (total_trips || 0) - active);

    return {
      labels: ['Active Trips', 'Completed Trips'],
      datasets: [{
        label: 'Trip Count',
        data: [active, completed],
        backgroundColor: [
          'rgb(34, 197, 94)',
          'rgb(239, 68, 68)',
        ],
        borderWidth: 0,
        borderRadius: 4,
      }]
    };
  };

  const barChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  const summaryCards = getSummaryCards();
  const tripTrendsData = getTripTrendsChartData();
  const deviceStatusData = getDeviceStatusChartData();

  return (
    <div className="p-6 space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Dashboard</h2>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-sm text-muted-foreground">Overview of your system performance</p>
            
          </div>
        </div>
        <div className="flex items-center gap-3 bg-muted/50">
          <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[200px] h-11 rounded-xl text-muted-foreground border-1 border-muted-foreground/20  ">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent className='border-1 border-muted-foreground/20  '>
              {periodOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4  ">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-32 bg-muted/70 rounded-xl animate-pulse"></div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4  ">
          {summaryCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card 
                key={index} 
                onClick={() => handleCardClick(card.route)}
                className={`group relative overflow-hidden border-1 border-muted-foreground/20 bg-muted/50  ${
                  card.route && (hasRole('super_admin') || hasRole('superadmin')) ? 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]' : ''
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${card.bgGradient} opacity-10 rounded-full blur-2xl`}></div>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-muted-foreground mb-2">{card.title}</p>
                      <h3 className="text-xl font-bold text-foreground mb-1">{card.value}</h3>
                      <p className="text-xs text-muted-foreground">{card.description}</p>
                    </div>
                    <div className={`p-2 rounded-xl bg-gradient-to-br ${card.bgGradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
        {/* Trip Trends Chart */}
        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300 bg-muted/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  Trip Trends
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-1">
                  Daily trip count over time
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-64 bg-muted/50 rounded-lg animate-pulse"></div>
            ) : (
              <div className="h-64">
                <Line data={tripTrendsData} options={lineChartOptions} />
              </div>
            )}
          </CardContent>
        </Card>

       
      </div>

      {/* Recent Alerts and Top Accounts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Alerts */}
        <Card className="lg:col-span-2 border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300 bg-muted/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Recent Alerts
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-1">
                  Latest system alerts and notifications
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : dashboardData?.recent_alerts && dashboardData.recent_alerts.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {dashboardData.recent_alerts.map((alert, index) => (
                  <div 
                    key={alert.id} 
                    className="p-4 rounded-lg border-1 border-muted-foreground/20 bg-background/50 hover:bg-background hover:shadow-md transition-all duration-200 animate-in slide-in-from-left-4"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${getAlertTypeColor(alert.alert_type)} text-white`}>
                          <AlertTriangle className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-sm text-foreground">{alert.alert_type}</h4>
                            {alert.raw_code && (
                              <Badge variant="outline" className="text-xs">
                                {alert.raw_code}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">
                            Terminal: {alert.terminal_id}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {alert.voltage !== undefined && (
                              <div className="flex items-center gap-1">
                                <Zap className="h-3 w-3" />
                                <span>{alert.voltage}V</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>{formatDateTime(alert.created_at || alert.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">No alerts found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Accounts */}
        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300 bg-muted/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-purple-500" />
                    Top Accounts
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-1">
                    Accounts with most devices
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse"></div>
                  ))}
                </div>
              ) : dashboardData?.top_accounts && dashboardData.top_accounts.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.top_accounts.map((account, index) => {
                    const totalDevices = dashboardData?.summary?.total_devices || 1;
                    const percentage = Math.round((account.total_devices / totalDevices) * 100);
                    
                    return (
                      <div 
                        key={index}
                        className="p-4 rounded-lg border-1 border-muted-foreground/20 bg-background/50 hover:bg-background hover:shadow-md transition-all duration-200 animate-in slide-in-from-right-4"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600">
                              <Building2 className="h-4 w-4" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground">{account.name}</h4>
                              <p className="text-xs text-muted-foreground">{account.total_devices} devices</p>
                            </div>
                          </div>
                          <Badge variant="dark" className="text-xs">
                            {percentage}%
                          </Badge>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                          <div 
                            className="h-2 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">No accounts found</p>
                </div>
              )}
            </CardContent>
          </Card>
      </div>

      {/* Additional CSS for animations */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
          background-size: 200% 100%;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
